// src/features/streams/useSimulcastMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import {
  SimulcastStatusType,
  type ISimulcastRtmpUpdateReq,
  type ISimulcastFBCreateReq,
  type ISimulcastFBUpdateReq,
  type ISimulcastYTCreateReq,
  type ISimulcastYTUpdateReq,
} from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { StreamApi } from './StreamApi';
import { SimulcastCacheUtils } from './simulcast/simulcastCache';

/**
 * Simulcast mutation hooks.
 * Each mutation patches the stream query cache, then invalidates as a safety fallback.
 */

export function useStartSimulcast(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (simulcastId: number) =>
      StreamApi.startSimulcast(selectedCId!, mediaId, simulcastId),
    onSuccess: (_data, simulcastId) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.patchSimulcastStatus(current, simulcastId, SimulcastStatusType.Started),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

export function useStopSimulcast(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (simulcastId: number) =>
      StreamApi.stopSimulcast(selectedCId!, mediaId, simulcastId),
    onSuccess: (_data, simulcastId) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.patchSimulcastStatus(current, simulcastId, SimulcastStatusType.Stopped),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

export function useDeleteSimulcast(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (simulcastId: number) =>
      StreamApi.deleteSimulcast(selectedCId!, mediaId, simulcastId),
    onSuccess: (updatedList) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.replaceSimulcasts(current, updatedList),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

export function useCreateSimulcastRtmp(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ISimulcastRtmpUpdateReq) =>
      StreamApi.createSimulcastRtmp(selectedCId!, mediaId, req),
    onSuccess: (updatedList) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.replaceSimulcasts(current, updatedList),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

interface UpdateSimulcastRtmpArgs {
  simulcastId: number;
  req: ISimulcastRtmpUpdateReq;
}

export function useUpdateSimulcastRtmp(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ simulcastId, req }: UpdateSimulcastRtmpArgs) =>
      StreamApi.updateSimulcastRtmp(selectedCId!, mediaId, simulcastId, req),
    onSuccess: (updatedList) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.replaceSimulcasts(current, updatedList),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

// --- Facebook mutations ---

export function useCreateSimulcastFB(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ISimulcastFBCreateReq) =>
      StreamApi.createSimulcastFB(selectedCId!, mediaId, req),
    onSuccess: (created) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.upsertSimulcast(current, created),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

interface UpdateSimulcastFBArgs {
  simulcastId: number;
  req: ISimulcastFBUpdateReq;
}

export function useUpdateSimulcastFB(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ simulcastId, req }: UpdateSimulcastFBArgs) =>
      StreamApi.updateSimulcastFB(selectedCId!, mediaId, simulcastId, req),
    onSuccess: (updated) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.upsertSimulcast(current, updated),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

// --- YouTube mutations ---

export function useCreateSimulcastYT(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ISimulcastYTCreateReq) =>
      StreamApi.createSimulcastYT(selectedCId!, mediaId, req),
    onSuccess: (created) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.upsertSimulcast(current, created),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}

interface UpdateSimulcastYTArgs {
  simulcastId: number;
  req: ISimulcastYTUpdateReq;
}

export function useUpdateSimulcastYT(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ simulcastId, req }: UpdateSimulcastYTArgs) =>
      StreamApi.updateSimulcastYT(selectedCId!, mediaId, simulcastId, req),
    onSuccess: (updated) => {
      const streamKey = MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId);
      const current = queryClient.getQueryData<StreamViewData>(streamKey);
      if (current) {
        queryClient.setQueryData(
          streamKey,
          SimulcastCacheUtils.upsertSimulcast(current, updated),
        );
      }
      queryClient.invalidateQueries({ queryKey: streamKey });
    },
  });
}
